'use client';

import { useEffect, useRef } from 'react';

/**
 * Sets a `--parallax-y` CSS variable on the returned ref as the element
 * scrolls through the viewport, for a background-image-moves-slower effect.
 * Written directly to the DOM (not React state) so scrolling never triggers
 * a re-render. Only active while the element intersects the viewport, and
 * disabled entirely under `prefers-reduced-motion: reduce`.
 */
export function useParallax<T extends HTMLElement>(speed = 0.15, maxOffsetPx = 30) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId = 0;
    let isVisible = false;

    const update = () => {
      rafId = 0;
      const rect = el.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const raw = (viewportCenter - elementCenter) * speed;
      const offset = Math.max(-maxOffsetPx, Math.min(maxOffsetPx, raw));
      el.style.setProperty('--parallax-y', `${offset}px`);
    };

    const onScroll = () => {
      if (!isVisible || rafId) return;
      rafId = requestAnimationFrame(update);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible) onScroll();
      },
      { rootMargin: '25% 0px' },
    );
    observer.observe(el);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [speed, maxOffsetPx]);

  return ref;
}
